// context/GrpcAuthInterceptor.ts
import { getSession, signIn } from "next-auth/react";

class AuthTokenManager {
  private static refreshPromise: Promise<string | null> | null = null;

  public static async getValidToken(): Promise<string | null> {
    const session = await getSession();
    if (session?.error === "RefreshAccessTokenError") {
      signIn();
      return null;
    }
    return session?.accessToken || null;
  }

  public static async handleTokenRefresh(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      window.dispatchEvent(new Event("visibilitychange"));
      await new Promise((resolve) => setTimeout(resolve, 500));
      const session = await getSession();
      this.refreshPromise = null;
      return session?.accessToken || null;
    })();

    return this.refreshPromise;
  }
}

export class GrpcUnifiedInterceptor {
  public intercept(request: any, invoker: any) {
    const metadata = request.getMetadata();

    // 1. Unary calls handle errors through the mutable callback function embedded inside the request object
    if (typeof request.getCallback === "function" && request.getCallback()) {
      const originalCallback = request.getCallback();
      request.setCallback((err: any, response: any) => {
        if (err && err.code === 16) { // 16 = Unauthenticated
          AuthTokenManager.handleTokenRefresh().then((newToken) => {
            if (newToken) {
              metadata["Authorization"] = `Bearer ${newToken}`;
              return invoker(request);
            }
            originalCallback(err, response);
          });
        } else {
          originalCallback(err, response);
        }
      });
    }

    // 2. Fetch the initial token and execute the call
    return AuthTokenManager.getValidToken().then((token) => {
      if (token) metadata["Authorization"] = `Bearer ${token}`;

      const result = invoker(request);

      // CRITICAL RUNTIME DUCK-TYPING CHECK:
      // If the result lacks an `.on` method, this is a Unary pipeline runner execution payload.
      // We must return it instantly without wrapping it, preventing the "stream.on is not a function" error.
      if (!result || typeof result.on !== "function") {
        return result;
      }

      // 3. Otherwise, it is a genuine Server Stream call. Handle proxy events securely.
      const listeners: { [key: string]: Function[] } = { data: [], status: [], end: [], error: [] };
      let activeStream = result;

      const streamProxy = {
        on: (type: string, callback: Function) => {
          if (listeners[type]) listeners[type].push(callback);
          if (activeStream && typeof activeStream.on === "function") {
            if (type === "error") {
              activeStream.on("error", (err: any) => handleStreamError(err, callback));
            } else {
              activeStream.on(type, callback);
            }
          }
          return streamProxy;
        },
        cancel: () => {
          if (activeStream && typeof activeStream.cancel === "function") activeStream.cancel();
        },
        removeListener: (type: string, callback: Function) => {
          if (listeners[type]) listeners[type] = listeners[type].filter((cb) => cb !== callback);
          if (activeStream && typeof activeStream.removeListener === "function") {
            activeStream.removeListener(type, callback);
          }
          return streamProxy;
        }
      };

      const handleStreamError = (err: any, originalErrorCallback: Function) => {
        if (err.code === 16) {
          AuthTokenManager.handleTokenRefresh().then((newToken) => {
            if (newToken) {
              metadata["Authorization"] = `Bearer ${newToken}`;
              const retriedStream = invoker(request);
              activeStream = retriedStream;

              Object.keys(listeners).forEach((type) => {
                listeners[type].forEach((cb) => {
                  if (type === "error") {
                    retriedStream.on("error", (e: any) => handleStreamError(e, cb));
                  } else {
                    retriedStream.on(type, cb);
                  }
                });
              });
            } else {
              originalErrorCallback(err);
            }
          });
        } else {
          originalErrorCallback(err);
        }
      };

      // Bind root events to the running stream socket
      activeStream.on("data", (data: any) => listeners.data.forEach(cb => cb(data)));
      activeStream.on("status", (status: any) => listeners.status.forEach(cb => cb(status)));
      activeStream.on("end", () => listeners.end.forEach(cb => cb()));
      activeStream.on("error", (err: any) => {
        if (listeners.error.length === 0) throw err;
        listeners.error.forEach(cb => handleStreamError(err, cb));
      });

      return streamProxy;
    });
  }
}

